import { useParams } from "react-router-dom";
import { Spinner } from "@/components/ui/spinner";
import { useSectionVisibility } from "@/hooks/useSectionVisibility";
import { useProfileData } from "@/hooks/useProfileData";
import { SectionsContainer } from "./sections/SectionsContainer";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export const ContactDetailsContainer = () => {
  const { id } = useParams<{ id: string }>();
  const { profile, loading, error: profileError, refetch } = useProfileData(id);
  const { sectionVisibility, toggleSectionVisibility, error: visibilityError, loading: visibilityLoading } = useSectionVisibility(id || '');
  const { toast } = useToast();

  const handleRefresh = async () => {
    try {
      await refetch();
      toast({
        title: "Refreshed",
        description: "Contact details have been refreshed",
      });
    } catch (error) {
      console.error("Error refreshing:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to refresh contact details"
      });
    }
  };

  if (!id) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>No contact ID provided</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading || visibilityLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (profileError || visibilityError) {
    const errorMessage = profileError?.message || visibilityError?.message || "An error occurred while loading contact details";
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
        <div className="flex justify-end">
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="space-y-4">
        <Alert>
          <AlertTitle>Contact Not Found</AlertTitle>
          <AlertDescription>
            The requested contact could not be found. It may have been deleted or you might not have permission to view it.
          </AlertDescription>
        </Alert>
        <div className="flex justify-end">
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
      <SectionsContainer
        key={profile.updated_at}
        profile={profile}
        sectionVisibility={sectionVisibility}
        toggleSectionVisibility={toggleSectionVisibility}
      />
    </div>
  );
};